import { ConnectedProfile } from "../types/customer";
import { debug } from "./debugLogger";

/** Per-post hints aligned by index with selectedPlatforms (e.g. which LinkedIn profile). */
export type PlatformMappingHint = { linkedinTarget?: "personal" | "business" };

// Main mapping function that delegates to specialized handlers
export function mapAllowedPlatforms(
  selectedPlatforms: string[],
  connectedProfiles?: ConnectedProfile[],
  perPostHints?: Array<PlatformMappingHint | undefined>
) {
  debug.log(`🔄 Mapping ${selectedPlatforms.length} platform(s)`);

  // Handle case when no connected profiles exist
  if (!Array.isArray(connectedProfiles) || connectedProfiles.length === 0) {
    debug.log(`⚠️ No connected profiles available`);
    return selectedPlatforms.map(platform => ({
      platform,
      business_profile_id: `default_${platform}`
    }));
  }

  // Log available profiles for debugging
  debug.log(`📋 Available profiles:`, connectedProfiles.map(p => ({
    platform: p.platform,
    hasDetails: Array.isArray(p.details) && p.details.length > 0,
    id: p.details?.[0]?.id || 'missing'
  })));

  // Map each platform using the appropriate handler
  const result = selectedPlatforms.map((platform, index) => {
    const hint = perPostHints?.[index];
    // Choose appropriate handler based on platform type
    if (platform.toUpperCase() === "YOUTUBE" || platform.toUpperCase() === "YOUTUBE_SHORTS") {
      return mapYouTubePlatform(platform, connectedProfiles);
    }
    else if (platform.toUpperCase() === "GMB") {
      return mapGMBPlatform(platform, connectedProfiles);
    }
    else if (platform.toUpperCase() === "LINKEDIN" || platform.toUpperCase() === "LINKEDIN_REELS") {
      return mapLinkedInPlatform(platform, connectedProfiles, hint?.linkedinTarget);
    }
    else {
      return mapStandardPlatform(platform, connectedProfiles);
    }
  });

  debug.log("✅ FINAL RESULT:", JSON.stringify(result));
  return result;
}

function findYouTubeProfile(connectedProfiles: ConnectedProfile[]): ConnectedProfile | undefined {
  return connectedProfiles.find(
    (p) => (p.platform.toUpperCase() === "YOUTUBE" || p.platform.toUpperCase() === "YOUTUBE_SHORTS") && 
           Array.isArray(p.details) && 
           p.details.length > 0
  );
}

// Add this new function to check platform IDs when platforms are selected
export function checkPlatformIds(selectedPlatforms: string[], connectedProfiles?: ConnectedProfile[]) {
  debug.log("🔍 Checking platform IDs for:", selectedPlatforms);
  
  // Map the platforms to their IDs
  const platformMappings = mapAllowedPlatforms(selectedPlatforms, connectedProfiles);
  
  // Create a result object with status for each platform
  const result = platformMappings.map(mapping => {
    const { platform, business_profile_id } = mapping;
    const isDefault = business_profile_id.startsWith('default_');
    
    // For YouTube platforms, add more detailed info
    if (platform.toUpperCase() === "YOUTUBE" || platform.toUpperCase() === "YOUTUBE_SHORTS") {
      return {
        platform,
        business_profile_id,
        isValid: !isDefault,
        status: isDefault ? 'NOT_CONNECTED' : 'CONNECTED',
        message: isDefault 
          ? `YouTube channel not connected. You'll need to connect your channel before posting.` 
          : `YouTube channel connected: ${business_profile_id}`
      };
    }
    
    // For other platforms, just return basic status
    return {
      platform,
      business_profile_id,
      isValid: !isDefault,
      status: isDefault ? 'NOT_CONNECTED' : 'CONNECTED'
    };
  });
  
  debug.log("Platform ID check results:", result);
  return result;
}


// Special case handler for YouTube platforms
export function mapYouTubePlatform(platform: string, connectedProfiles: ConnectedProfile[]) {
  debug.log(`🎬 Mapping YouTube platform: "${platform}"`);
  
  // Find YouTube profile - always looking for regular "YOUTUBE" regardless of which YouTube platform was selected
  const youtubeProfile = connectedProfiles.find(p => 
    p.platform.toUpperCase() === "YOUTUBE" && 
    Array.isArray(p.details) && 
    p.details.length > 0 &&
    p.details[0].id
  );
  
  let business_profile_id = `default_${platform}`;
  
  if (youtubeProfile?.details?.[0]?.id) {
    business_profile_id = youtubeProfile.details[0].id;
    debug.log(`✅ Found YouTube channel ID: ${business_profile_id}`);
  } else {
    debug.log(`❌ No YouTube channel ID found, using default: ${business_profile_id}`);
  }
  
  // Set content type based on which YouTube platform
  const content_type = platform.toUpperCase() === "YOUTUBE" ? "LONG_FORM" : "SHORT_FORM";
  
  return { 
    platform, 
    business_profile_id, 
    content_type 
  };
}

// Special case handler for GMB platform
export function mapGMBPlatform(platform: string, connectedProfiles: ConnectedProfile[]) {
  debug.log(`🗺️ Mapping GMB platform: "${platform}"`);
  
  const gmbProfile = connectedProfiles.find(p => 
    p.platform.toUpperCase() === "GMB" && 
    Array.isArray(p.details) && 
    p.details.length > 0 &&
    p.details[0].id
  );
  
  let business_profile_id = `default_${platform}`;
  let gmb_account_id: string | undefined;
  
  if (gmbProfile?.details?.[0]?.id) {
    business_profile_id = gmbProfile.details[0].id;
    gmb_account_id = gmbProfile.details[0].metadata?.account_id;
    debug.log(`✅ Found GMB profile ID: ${business_profile_id}, account ID: ${gmb_account_id}`);
  } else {
    debug.log(`❌ No GMB profile found, using default: ${business_profile_id}`);
  }
  
  return { 
    platform, 
    business_profile_id, 
    gmb_account_id 
  };
}

/** Detect which LinkedIn targets the customer has connected (person vs organization URN). */
export function getConnectedLinkedInTargets(
  connectedProfiles?: ConnectedProfile[]
): { personal: boolean; business: boolean } {
  const profiles = Array.isArray(connectedProfiles) ? connectedProfiles : [];
  const linkedInProfiles = profiles.filter(
    (p) => p.platform.toUpperCase() === "LINKEDIN" && Array.isArray(p.details)
  );
  const business = linkedInProfiles.some((p) =>
    p.details.some((d) => d.id?.startsWith("urn:li:organization:"))
  );
  // Personal id is the bare member id from /v2/userinfo `sub` (no urn:li:person: prefix),
  // so "personal" is any LinkedIn detail that isn't an organization.
  const personal = linkedInProfiles.some((p) =>
    p.details.some((d) => d.id && !d.id.startsWith("urn:li:organization:"))
  );
  return { personal, business };
}

/**
 * LinkedIn: resolve the target profile's business_profile_id.
 *
 * Personal and Business Page both store under platform "LINKEDIN" and are told apart only by
 * the detail id URN (urn:li:person: vs urn:li:organization:). `target` (from the per-card
 * toggle) picks which one; when omitted we prefer the Business Page if connected, else personal —
 * preserving the previous auto behavior. Falls back to the other target if the requested one
 * isn't connected.
 */
export function mapLinkedInPlatform(
  platform: string,
  connectedProfiles: ConnectedProfile[],
  target?: "personal" | "business"
) {
  debug.log(`💼 Mapping LinkedIn platform: "${platform}" (target: ${target ?? "auto"})`);

  const linkedInProfiles = connectedProfiles.filter(
    (p) => p.platform.toUpperCase() === "LINKEDIN" && Array.isArray(p.details) && p.details.length > 0
  );

  const findOrg = () => {
    const orgProfile = linkedInProfiles.find((p) =>
      p.details.some((d) => d.id?.startsWith("urn:li:organization:"))
    );
    return orgProfile?.details.find((d) => d.id?.startsWith("urn:li:organization:"))?.id;
  };
  const findPersonal = () => {
    // Personal id is the bare member id (no urn:li:person: prefix), so it's any
    // LinkedIn detail that isn't an organization URN.
    const isPersonal = (d: { id?: string }) =>
      !!d.id && !d.id.startsWith("urn:li:organization:");
    const personalProfile = linkedInProfiles.find((p) => p.details.some(isPersonal));
    return personalProfile?.details.find(isPersonal)?.id;
  };

  // Honor the explicit target first, then fall back to the other if it isn't connected.
  const order: Array<"personal" | "business"> =
    target === "personal" ? ["personal", "business"] : ["business", "personal"];

  for (const t of order) {
    const id = t === "business" ? findOrg() : findPersonal();
    if (id) {
      debug.log(`✅ LinkedIn ${t} profile: ${id}`);
      return { platform, business_profile_id: id };
    }
  }

  debug.log(`❌ No LinkedIn profile found, using default`);
  return { platform, business_profile_id: `default_${platform}` };
}

/**
 * Reel/variant platforms share the same connected profile as their base platform.
 * Business profile ID is the same whether the post is reel or static (e.g. INSTAGRAM for both feed and reels).
 */
function getBasePlatformForLookup(platform: string): string {
  const upper = platform.toUpperCase();
  if (upper === "INSTAGRAM_REELS") return "INSTAGRAM";
  if (upper === "FACEBOOK_REELS") return "FACEBOOK";
  if (upper === "LINKEDIN_REELS") return "LINKEDIN";
  if (upper === "TIKTOK_REELS") return "TIKTOK";
  return upper;
}

// Generic handler for standard platforms
export function mapStandardPlatform(platform: string, connectedProfiles: ConnectedProfile[]) {
  debug.log(`📱 Mapping standard platform: "${platform}"`);
  const lookupPlatform = getBasePlatformForLookup(platform);

  const profile = connectedProfiles.find(
    (p) =>
      p.platform.toUpperCase() === lookupPlatform &&
      Array.isArray(p.details) &&
      p.details.length > 0 &&
      p.details[0].id
  );

  let business_profile_id = `default_${platform}`;

  if (profile?.details?.[0]?.id) {
    business_profile_id = profile.details[0].id;
    debug.log(`✅ Found profile ID for ${platform} (lookup: ${lookupPlatform}): ${business_profile_id}`);
  } else {
    debug.log(`❌ No profile found for ${platform} (lookup: ${lookupPlatform}), using default: ${business_profile_id}`);
  }

  return { platform, business_profile_id };
}

/**
 * True when the customer has a connected profile with a usable business profile id for this platform,
 * consistent with {@link mapAllowedPlatforms} (YouTube Shorts uses the YouTube channel mapping).
 */
export function isConnectedForPublishPlatform(
  platform: string,
  connectedProfiles?: ConnectedProfile[]
): boolean {
  if (!Array.isArray(connectedProfiles) || connectedProfiles.length === 0) {
    return false;
  }
  const upper = platform.toUpperCase();
  if (upper === "YOUTUBE" || upper === "YOUTUBE_SHORTS") {
    const youtubeProfile = connectedProfiles.find(
      (p) =>
        p.platform.toUpperCase() === "YOUTUBE" &&
        Array.isArray(p.details) &&
        p.details.length > 0 &&
        p.details[0].id
    );
    return Boolean(youtubeProfile);
  }

  if (upper === "LINKEDIN") {
    const { business_profile_id } = mapLinkedInPlatform(platform, connectedProfiles);
    return !business_profile_id.startsWith("default_");
  }

  const lookupPlatform = getBasePlatformForLookup(platform);
  const profile = connectedProfiles.find(
    (p) =>
      p.platform.toUpperCase() === lookupPlatform &&
      Array.isArray(p.details) &&
      p.details.length > 0 &&
      p.details[0].id
  );
  return Boolean(profile);
}